/* eslint-disable no-nested-ternary */
import { useState, useTransition } from 'react'
import { Box, Grid, IconButton, useTheme } from '@mui/material'
import { withStyles } from '@mui/styles'
import Icon from 'ui/Element/Icon'
import { getThemeColor } from 'lib/util'

const styles = theme => {
  const { palette } = theme

  const base = {
    transition: [
      'background-color 100ms ease, color 250ms ease',
      'border-color 100ms ease, color 250ms ease',
    ],
    borderWidth: '0.125rem',
    borderStyle: 'solid',
    margin: '0.375rem',
    padding: '0.5rem',
    borderColor: palette.primary.dark,
  }

  return {
    actionLinks: {
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    contained: {
      ...base,
      backgroundColor: palette.primary.dark,
      '&:hover': {
        backgroundColor: [[palette.primary.contrastText], '!important'],
      },
    },
    outlined: {
      ...base,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: [[palette.primary.dark], '!important'],
      },
    },
    ...theme.custom.actionLinks,
  }
}

const iconColor = (variant, hover) => (variant === 'contained'
  ? (hover && 'primary.dark') || 'text.inverted'
  : (hover && 'primary.contrastText') || 'primary.dark')

/**
 * QuickAction Layout element
 *
 * A quick action button intended to be displayed in the {@link MuiAppBar}.
 *
 * @param {Object} p
 * @param {string} p.key                     key of the quick action
 * @param {boolean} p.hidden                 whether the quick action should be hidden
 * @param {string} p.title                   title of the quick action
 * @param {Object} p.classes                 css class name to be used by the element
 * @param {'contained'|'outlined'} p.variant variant of the button
 * @param {boolean} p.active                 whether the quick action is in an active state
 * @param {string} p.background              background color
 * @param {string} p.color                   color when in normal state
 * @param {string} p.colorActive             color when in active state
 * @param {string} p.icon                    icon when in normal state
 * @param {string} p.iconActive              icon when in active state
 * @param {boolean} p.spaced                 whether the action should be spaced
 * @param {Gaia.Component.Events} p.events   events for the component
 * @returns {JSX.Element|null}  a function waiting for the element's props
 */
const QuickAction = ({
  title,
  classes,
  variant,
  active,
  background,
  color,
  colorActive,
  icon,
  iconActive,
  spaced,
  events,
  ...props
}) => {
  const [, startTransition] = useTransition()
  const [hover, setHover] = useState(false)

  const defaultColor = iconColor(variant, hover)
  const handleClick = event => startTransition(() => events?.onClick(event))

  return props.hidden ? null : (
    <Grid
      item
      component={IconButton}
      key={props.key}
      title={title}
      {...variant !== null && {
        className: classes[variant || 'contained'],
      }}
      onClick={handleClick}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      sx={{
        marginLeft: spaced ? 40 : false,
        ...background
          ? {
            backgroundColor: theme => `${getThemeColor(theme, background)}!important`,
            border: 'none!important',
          }
          : {},
      }}
    >
      <Icon
        background={background}
        source={active ? iconActive : icon}
        color={active ? colorActive : color || defaultColor}
      />
    </Grid>
  )
}

/**
 * ActionLinks Layout component.
 *
 * A sequence of quick action buttons intended to be displayed in the {@link MuiAppBar}.
 *
 * To use them, an action must include a `quickActions` array of objects, each of which must
 * contain props for every {@link QuickAction} element to be created.
 *
 * @param {Object} p
 * @param {Object[]} p.actions  an array containing the properties of the actions to be displayed
 * @param {Object} p.classes    the classes to be used, defined in {@link styles}
 * @param {boolean} [p.hidden]  whether the element should not be rendered
 * @returns {JSX.Element|null}
 */
const ActionLinks = ({ actions, classes, hidden }) => (hidden ? null : (
  <Grid
    item
    container
    component={Box}
    className={classes.actionLinks}
    xs={'auto'}
  >
    {actions?.map(props => (
      <QuickAction
        {...props}
        classes={classes}
      />
    ))}
  </Grid>
))

export default withStyles(styles)(ActionLinks)
